import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Container } from "../components/ui/Container";
import { Stack } from "../components/ui/Stack";
import { Heading } from "../components/ui/Heading";
import { Text } from "../components/ui/Text";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Price } from "../components/ui/Price";
import { Spinner } from "../components/ui/Spinner";
import { Alert } from "../components/ui/Alert";
import { CheckoutSteps } from "../components/CheckoutSteps";
import { useApi } from "../context/ApiContext";
import { useCurrency } from "../context/CurrencyContext";
import { useCart } from "../context/CartContext";
import { useTranslation } from "react-i18next";
import { checkoutStepConfig } from "../utils/checkoutSteps";

const openWhatsApp = (phoneNumber: string, orderNumber: string): void => {
  // Remove any non-digit characters except +
  const cleanPhone = phoneNumber.replace(/[^\d+]/g, "");
  // Remove + if present and add it back, ensure it starts with +
  const formattedPhone = cleanPhone.startsWith("+") ? cleanPhone : `+${cleanPhone}`;
  const message = encodeURIComponent(`Hello! I have a question about my order ${orderNumber}.`);
  const whatsappUrl = `https://wa.me/${formattedPhone.replace("+", "")}?text=${message}`;
  window.open(whatsappUrl, "_blank");
};

export const OrderConfirmation: React.FC = () => {
 const { t } = useTranslation();
 const [searchParams] = useSearchParams();
 const navigate = useNavigate();
 const { api } = useApi();
 const { currency } = useCurrency();
 const { refreshCart } = useCart();

 const orderId = searchParams.get("orderId");
 const paypalToken = searchParams.get("token"); // PayPal order ID
 const [order, setOrder] = useState<any>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState<string | null>(null);
 const [loadingInvoice, setLoadingInvoice] = useState(false);
 const [confirmingPayment, setConfirmingPayment] = useState(false);

 useEffect(() => {
  if (orderId) {
   fetchOrder();
   // Refresh cart to ensure it's cleared (backend already cleared it during payment authorization)
   refreshCart();
  } else if (paypalToken) {
   // Handle PayPal redirect
   handlePayPalRedirect();
  } else {
   setError(t("orderConfirmation.orderIdNotProvided"));
   setLoading(false);
  }
 }, [orderId, paypalToken, refreshCart, t]);

 const fetchOrder = useCallback(async () => {
  try {
   const response = await api.get(`/orders/${orderId}`);
   setOrder(response.data);
  } catch (err: any) {
   setError(err.response?.data?.error || t("orderConfirmation.failedToLoadOrder"));
  } finally {
   setLoading(false);
  }
 }, [api, orderId, t]);

 const handlePayPalRedirect = useCallback(async () => {
  try {
   setConfirmingPayment(true);
   // Get order by PayPal token (payment intent ID)
   const response = await api.get(`/payments/order-by-intent/${paypalToken}`);
   const { order: foundOrder, payment, needsConfirmation } = response.data;

   if (needsConfirmation && payment) {
    // Confirm the PayPal payment
    try {
     await api.post("/payments/confirm", {
      paymentId: payment._id,
      paymentIntentId: paypalToken,
      method: "paypal",
     });
    } catch (confirmErr: any) {
     // If confirmation fails, still show the order but log the error
     console.error("Failed to confirm PayPal payment:", confirmErr);
    }
   }

   setOrder(foundOrder);
   await refreshCart();
  } catch (err: any) {
   setError(err.response?.data?.error || t("orderConfirmation.failedToLoadOrder"));
  } finally {
   setLoading(false);
   setConfirmingPayment(false);
  }
 }, [api, paypalToken, refreshCart, t]);

 // Determine step context from order data
 const stepContext = useMemo(() => {
  // Check if order is digital-only by checking if all products are digital
  // This matches the logic used in Checkout.tsx
  let isDigitalOnlyOrder = false;
  if (order && order.items && Array.isArray(order.items) && order.items.length > 0) {
   isDigitalOnlyOrder = order.items.every((item: any) => {
    const product = item.product;
    return product && product.isDigital === true;
   });
  }
  
  // Get payment method from order
  const selectedPaymentMethod = order?.paymentMethod || null;
  
  return {
   isDigitalOnlyOrder,
   selectedPaymentMethod,
   step: 999, // Use high number for confirmation page (doesn't affect step selection)
  };
 }, [order]);

 // Get steps using the unified step configuration
 const steps = useMemo(
  () => checkoutStepConfig.getStepsForHeader(stepContext),
  [stepContext]
 );

 // Get current step number (should be the confirmation step, which is the last step)
 const currentStep = useMemo(() => {
  const availableSteps = checkoutStepConfig.getAvailableSteps(stepContext);
  const confirmationStepIndex = availableSteps.findIndex((s) => s.id === "confirmation");
  return confirmationStepIndex >= 0 ? confirmationStepIndex + 1 : availableSteps.length;
 }, [stepContext]);

 if (loading || confirmingPayment) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
     {confirmingPayment && (
      <Text>{t("orderConfirmation.confirmingPayment") || "Confirming payment..."}</Text>
     )}
    </Stack>
   </Container>
  );
 }

 if (error || !order) {
  return (
   <Container>
    <Alert variant="error">{error || t("orderConfirmation.orderNotFound")}</Alert>
    <Button onClick={() => navigate("/products")} className="mt-4">
     {t("orderConfirmation.continueShopping")}
    </Button>
   </Container>
  );
 }

 return (
  <div className="w-full bg-white py-8">
   <div className="max-w-4xl mx-auto px-4 md:px-8">
    <Heading size="2xl" className="mb-8 text-store-primary">
     {t("orderConfirmation.title")}
    </Heading>

    <CheckoutSteps currentStep={currentStep} steps={steps} />

    <Stack spacing="lg" align="center">
     <div className="text-center">
      <Heading size="2xl" className="mb-4">{t("orderConfirmation.thankYou")}</Heading>
      {order.status === "pending" && order.payment?.status === "authorized" ? (
       <>
        <Text size="lg" className="text-gray-600 mb-2">
         {t("orderConfirmation.paymentAuthorized")}
        </Text>
        <Text size="md" className="text-gray-500 mb-2">
         {t("orderConfirmation.paymentPendingCapture")}
        </Text>
       </>
      ) : order.status === "confirmed" ? (
       <Text size="lg" className="text-gray-600">
        {t("orderConfirmation.orderConfirmed")}
       </Text>
      ) : (
       <Text size="lg" className="text-gray-600">
        {t("orderConfirmation.orderReceived")}
       </Text>
      )}
      <Text className="mt-2">
       {t("orderConfirmation.orderNumber")}: <Text weight="semibold">{order.orderNumber}</Text>
      </Text>
      {order.status === "pending" && (
       <Text size="sm" className="text-gray-500 mt-2">
        {t("orderConfirmation.statusPendingConfirmation")}
       </Text>
      )}
     </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full max-w-4xl">
     <Card padding="lg">
      <Stack spacing="md">
       <Heading size="lg">{t("checkout.orderSummary")}</Heading>
       <div className="space-y-4">
        {order.items?.map((item: any) => (
         <div key={item._id} className="flex justify-between items-start">
          <div className="flex-1">
           <Text weight="semibold">{item.productTitle}</Text>
           {item.variantName && (
            <Text size="sm" className="text-gray-500">
             {t("checkout.variantLabel")}{item.variantName}
            </Text>
           )}
           <Text size="sm" className="text-gray-500">
            {t("checkout.quantityLabel")}{item.quantity}
           </Text>
          </div>
          <Price amount={item.price * item.quantity} currency={currency} />
         </div>
        ))}
       </div>
       <hr />
       <div className="space-y-2">
        <div className="flex justify-between">
         <Text>{t("checkout.subtotal")}</Text>
         <Price amount={order.subtotal} currency={currency} />
        </div>
        {order.tax > 0 && (
         <div className="flex justify-between">
          <Text>{t("checkout.tax")}</Text>
          <Price amount={order.tax} currency={currency} />
         </div>
        )}
        <div className="flex justify-between">
         <Text>{t("checkout.shipping")}</Text>
         <Price amount={order.shipping || 0} currency={currency} />
        </div>
        <hr />
        <div className="flex justify-between">
         <Text weight="semibold" size="lg">{t("checkout.total")}</Text>
         <Price amount={order.total} currency={currency} size="lg" />
        </div>
       </div>
      </Stack>
     </Card>

     <div className="space-y-6">
      {order.shippingAddress && (
       <Card padding="lg">
        <Stack spacing="md">
         <Heading size="lg">{t("checkout.shippingAddress")}</Heading>
         <div className="text-sm">
          <Text>
           {order.shippingAddress.firstName} {order.shippingAddress.lastName}
          </Text>
          <Text>{order.shippingAddress.address1}</Text>
          {order.shippingAddress.address2 && (
           <Text>{order.shippingAddress.address2}</Text>
          )}
          <Text>
           {order.shippingAddress.city}, {order.shippingAddress.province}{" "}
           {order.shippingAddress.postalCode}
          </Text>
          <Text>{order.shippingAddress.country}</Text>
          {order.shippingAddress.phone && (
           <Text>{t("checkout.phoneLabel")}{order.shippingAddress.phone}</Text>
          )}
         </div>
        </Stack>
       </Card>
      )}

      {order.shippingMethod && (
       <Card padding="lg">
        <Stack spacing="md">
         <Heading size="lg">{t("checkout.shippingMethod")}</Heading>
         <Text>{order.shippingMethod.name}</Text>
         {order.shippingMethod.estimatedDays && (
          <Text size="sm" className="text-gray-500">
           {t("checkout.estimatedDelivery")}{order.shippingMethod.estimatedDays.min}-
           {order.shippingMethod.estimatedDays.max} {t("checkout.days")}
          </Text>
         )}
        </Stack>
       </Card>
      )}

      <Card padding="lg">
       <Stack spacing="md">
        <Heading size="lg">{t("orderConfirmation.orderStatus") || "Order Status"}</Heading>
        <Text weight="semibold" className="capitalize">
         {order.status}
        </Text>
       </Stack>
      </Card>
     </div>
    </div>

     <Stack direction="row" spacing="md">
      {order.invoiceKey && order.status === "confirmed" && (
      <Button
       variant="outline"
       onClick={async () => {
        try {
         setLoadingInvoice(true);
         // Download invoice PDF from bucket
         const response = await api.get(`/invoices/order/${order._id}/pdf`, {
          responseType: "blob", // Get as blob for PDF download
         });

         // Create a blob URL and trigger download
         const blob = new Blob([response.data], { type: "application/pdf" });
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement("a");
         link.href = url;
         link.setAttribute("download", `invoice-${order.orderNumber}.pdf`);
         document.body.appendChild(link);
         link.click();
         link.remove();
         window.URL.revokeObjectURL(url);
         } catch (err: any) {
         alert(err.response?.data?.error || err.response?.data?.message || t("orderConfirmation.failedToLoadInvoice"));
        } finally {
         setLoadingInvoice(false);
        }
       }}
       disabled={loadingInvoice}
      >
       {loadingInvoice ? t("common.loading") : t("orderConfirmation.viewInvoice")}
      </Button>)}
      {order.locationId && typeof order.locationId === "object" && order.locationId.whatsappPhoneNumber && (
      <Button
       variant="outline"
       onClick={() => openWhatsApp(order.locationId.whatsappPhoneNumber, order.orderNumber)}
       className="bg-green-50 text-green-700 border-green-500 hover:bg-green-100"
      >
       📱 WhatsApp
      </Button>
      )}
      <Button variant="outline" onClick={() => navigate("/orders")}>
       {t("orderConfirmation.viewAllOrders")}
      </Button>
      <Button onClick={() => navigate("/products")}>{t("orderConfirmation.continueShopping")}</Button>
     </Stack>
    </Stack>
   </div>
  </div>
 );
};

export default OrderConfirmation;

